import { CountUp } from "countup.js";

export default function drawsFilter() {
  const containers = document.querySelectorAll("[data-draws-filter]");
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const formatCountdownValue = (unit, value) => {
    const roundedValue = Math.max(0, Math.round(value));
    if (unit === "days") return String(roundedValue);
    return String(roundedValue).padStart(2, "0");
  };

  const getCountdownParts = (announcementTs) => {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const totalSeconds = Math.max(announcementTs - nowSeconds, 0);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      totalSeconds,
      showSeconds: totalSeconds < 3600,
      values: {
        days,
        hours,
        minutes,
        seconds,
      },
    };
  };

  containers.forEach((container) => {
    const filterButtons = Array.from(
      container.querySelectorAll("[data-round-slug]")
    );
    const matches = Array.from(container.querySelectorAll("[data-draw-round]"));
    const introElement = container.querySelector("[data-draws-intro]");
    const matchesElement = container.querySelector(".draws--matches");
    const revealStatusElement = container.querySelector("[data-draws-reveal-status]");
    const countdownElement = container.querySelector("[data-draws-countdown]");
    const countdownGrid = container.querySelector("[data-draws-countdown-grid]");
    const countdownUnits = Array.from(
      container.querySelectorAll("[data-countdown-unit]")
    );
    const introTemplates = Array.from(
      container.querySelectorAll("template[data-round-intro]")
    );
    const roundIntroMap = new Map(
      introTemplates.map((tpl) => [
        tpl.dataset.roundIntro,
        tpl.innerHTML.trim(),
      ])
    );
    const roundAnnouncementMap = new Map(
      filterButtons.map((button) => {
        const announcementTs = Number.parseInt(
          button.getAttribute("data-announcement-ts") || "",
          10
        );

        return [
          button.getAttribute("data-round-slug"),
          Number.isNaN(announcementTs) ? null : announcementTs,
        ];
      })
    );
    const roundSkipRevealMap = new Map(
      filterButtons.map((button) => [
        button.getAttribute("data-round-slug"),
        button.getAttribute("data-skip-reveal") === "true",
      ])
    );
    const countdownValueElements = new Map(
      Array.from(container.querySelectorAll("[data-countdown-value]")).map(
        (element) => [element.getAttribute("data-countdown-value"), element]
      )
    );
    const countdownDisplayValues = new Map(
      Array.from(countdownValueElements.keys()).map((unit) => [unit, null])
    );
    let countdownIntervalId = null;
    let revealObserver = null;
    let revealMessageIntervalId = null;
    let revealRunId = 0;
    const revealTimeouts = [];
    const revealPhrases = [
      "Matching teams for this fixture",
      "Building the next head-to-head",
      "Pairing opponents now",
      "Setting the next matchup",
      "Assigning teams to this draw",
    ];
    const revealCutoffAfterAnnouncementSeconds = 5 * 60;
    const revealLeftDelayMs = 0;
    const revealVsDelayMs = 3000;
    const revealRightDelayMs = 6000;

    if (!filterButtons.length) return;

    const twigActiveButton = filterButtons.find((button) =>
      button.classList.contains("active")
    );
    const firstEnabledButton = filterButtons.find((btn) => !btn.disabled);
    let activeRoundSlug = null;

    const getRoundSlugFromHash = () => {
      const hash = (window.location.hash || "").replace(/^#/, "");
      if (!hash) return null;
      try {
        return decodeURIComponent(hash);
      } catch {
        return hash;
      }
    };

    const findEnabledButtonForSlug = (slug) => {
      if (!slug) return null;
      return (
        filterButtons.find(
          (button) =>
            button.getAttribute("data-round-slug") === slug && !button.disabled
        ) || null
      );
    };

    const updateHashForRound = (slug) => {
      if (!slug || typeof window === "undefined") return;
      const nextUrl = `${window.location.pathname}${window.location.search}#${encodeURIComponent(slug)}`;
      window.history.replaceState(null, "", nextUrl);
    };

    const updateIntro = () => {
      if (!introElement) return;
      const html =
        (activeRoundSlug && roundIntroMap.get(activeRoundSlug)) || "";
      introElement.innerHTML = html;
    };

    const stopCountdown = () => {
      if (countdownIntervalId) {
        window.clearInterval(countdownIntervalId);
        countdownIntervalId = null;
      }
    };

    const clearRevealTimeouts = () => {
      while (revealTimeouts.length) {
        const timeoutId = revealTimeouts.pop();
        window.clearTimeout(timeoutId);
      }
    };

    const stopRevealCycle = () => {
      revealRunId += 1;
      clearRevealTimeouts();

      if (revealObserver) {
        revealObserver.disconnect();
        revealObserver = null;
      }

      if (revealMessageIntervalId) {
        window.clearInterval(revealMessageIntervalId);
        revealMessageIntervalId = null;
      }

      if (revealStatusElement) {
        if (matchesElement) {
          matchesElement.insertBefore(revealStatusElement, matchesElement.firstChild);
        }
        revealStatusElement.hidden = true;
        revealStatusElement.textContent = "";
        revealStatusElement.classList.remove("is-visible", "is-fading");
      }
    };

    const setCountdownValue = (unit, nextValue) => {
      const element = countdownValueElements.get(unit);
      if (!element) return;

      const previousValue = countdownDisplayValues.get(unit);
      if (previousValue === nextValue) return;

      const formattedValue = formatCountdownValue(unit, nextValue);

      if (
        prefersReducedMotion ||
        previousValue === null ||
        Math.abs(previousValue - nextValue) > 10
      ) {
        element.textContent = formattedValue;
        countdownDisplayValues.set(unit, nextValue);
        return;
      }

      const counter = new CountUp(element, nextValue, {
        duration: 0.45,
        formattingFn: (value) => formatCountdownValue(unit, value),
        startVal: previousValue,
        useEasing: true,
      });

      if (!counter.error) {
        counter.start();
      } else {
        element.textContent = formattedValue;
      }

      countdownDisplayValues.set(unit, nextValue);
    };

    const updateCountdownDisplay = (announcementTs) => {
      if (!countdownElement || !countdownGrid) return false;

      const countdown = getCountdownParts(announcementTs);
      const countdownComplete = countdown.totalSeconds <= 0;
      const showSeconds = countdown.showSeconds && !countdownComplete;

      countdownGrid.setAttribute(
        "data-show-seconds",
        showSeconds ? "true" : "false"
      );

      countdownUnits.forEach((unitElement) => {
        const unit = unitElement.getAttribute("data-countdown-unit");
        unitElement.hidden = unit === "seconds" ? !showSeconds : false;
      });

      Object.entries(countdown.values).forEach(([unit, value]) => {
        if (unit === "seconds" && !showSeconds) return;
        setCountdownValue(unit, value);
      });

      return !countdownComplete;
    };

    const getVisibleMatches = () => {
      if (!matches.length) return;
      const visibleMatches = [];

      matches.forEach((match) => {
        const matchRound = match.getAttribute("data-draw-round");
        if (!activeRoundSlug || matchRound === activeRoundSlug) {
          match.hidden = false;
          match.classList.remove(
            "draws--match--revealed",
            "draws--match--reveal-vs",
            "draws--match--reveal-left",
            "draws--match--reveal-right"
          );
          visibleMatches.push(match);
        } else {
          match.hidden = true;
          match.classList.remove(
            "draws--match--revealed",
            "draws--match--reveal-vs",
            "draws--match--reveal-left",
            "draws--match--reveal-right"
          );
        }
      });

      return visibleMatches;
    };

    const isElementInViewport = (element) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      return rect.top < viewportHeight * 0.9 && rect.bottom > viewportHeight * 0.1;
    };

    const showRandomRevealPhrase = () => {
      if (!revealStatusElement) return;
      const phrase =
        revealPhrases[Math.floor(Math.random() * revealPhrases.length)] ||
        revealPhrases[0];
      revealStatusElement.textContent = phrase;
      revealStatusElement.hidden = false;
      revealStatusElement.classList.remove("is-fading");
      revealStatusElement.classList.add("is-visible");
    };

    const runStaggeredReveal = (visibleMatches, runId) => {
      const revealGapMs = 8000;
      const statusFadeLeadMs = 450;
      if (!visibleMatches.length || runId !== revealRunId) return;

      if (prefersReducedMotion) {
        visibleMatches.forEach((match) => {
          match.classList.add(
            "draws--match--revealed",
            "draws--match--reveal-vs",
            "draws--match--reveal-left",
            "draws--match--reveal-right"
          );
        });
        if (revealStatusElement) revealStatusElement.hidden = true;
        return;
      }

      const queueRevealForIndex = (index, delayMs) => {
        const timeoutId = window.setTimeout(() => {
          revealMatchAtIndex(index);
        }, delayMs);
        revealTimeouts.push(timeoutId);
      };

      const revealMatchAtIndex = (index) => {
        if (runId !== revealRunId) return;

        const match = visibleMatches[index];
        if (!match || match.hidden) return;
        match.classList.add("draws--match--revealed");

        const vsTimeoutId = window.setTimeout(() => {
          if (runId !== revealRunId || match.hidden) return;
          match.classList.add("draws--match--reveal-vs");
        }, revealVsDelayMs);
        revealTimeouts.push(vsTimeoutId);

        const leftTimeoutId = window.setTimeout(() => {
          if (runId !== revealRunId || match.hidden) return;
          match.classList.add("draws--match--reveal-left");
        }, revealLeftDelayMs);
        revealTimeouts.push(leftTimeoutId);

        const rightTimeoutId = window.setTimeout(() => {
          if (runId !== revealRunId || match.hidden) return;
          match.classList.add("draws--match--reveal-right");
        }, revealRightDelayMs);
        revealTimeouts.push(rightTimeoutId);

        const hasNextMatch = index < visibleMatches.length - 1;
        if (!hasNextMatch) {
          if (revealMessageIntervalId) {
            window.clearInterval(revealMessageIntervalId);
            revealMessageIntervalId = null;
          }
          if (revealStatusElement) revealStatusElement.hidden = true;
          return;
        }

        const nextMatch = visibleMatches[index + 1];
        if (matchesElement && revealStatusElement && nextMatch) {
          matchesElement.insertBefore(revealStatusElement, nextMatch);
        }
        showRandomRevealPhrase();
        if (revealMessageIntervalId) {
          window.clearInterval(revealMessageIntervalId);
        }
        revealMessageIntervalId = window.setInterval(showRandomRevealPhrase, 1400);

        const fadeTimeoutId = window.setTimeout(() => {
          if (!revealStatusElement || runId !== revealRunId) return;
          revealStatusElement.classList.remove("is-visible");
          revealStatusElement.classList.add("is-fading");
        }, Math.max(0, revealGapMs - statusFadeLeadMs));
        revealTimeouts.push(fadeTimeoutId);

        const timeoutId = window.setTimeout(() => {
          if (revealMessageIntervalId) {
            window.clearInterval(revealMessageIntervalId);
            revealMessageIntervalId = null;
          }
          if (revealStatusElement) {
            revealStatusElement.hidden = true;
            revealStatusElement.classList.remove("is-visible", "is-fading");
          }
          queueRevealForIndex(index + 1, 0);
        }, revealGapMs);
        revealTimeouts.push(timeoutId);
      };

      if (revealStatusElement && matchesElement && visibleMatches[0]) {
        matchesElement.insertBefore(revealStatusElement, visibleMatches[0]);
      }
      showRandomRevealPhrase();
      if (revealMessageIntervalId) {
        window.clearInterval(revealMessageIntervalId);
      }
      revealMessageIntervalId = window.setInterval(showRandomRevealPhrase, 1400);

      const initialFadeTimeoutId = window.setTimeout(() => {
        if (!revealStatusElement || runId !== revealRunId) return;
        revealStatusElement.classList.remove("is-visible");
        revealStatusElement.classList.add("is-fading");
      }, Math.max(0, revealGapMs - statusFadeLeadMs));
      revealTimeouts.push(initialFadeTimeoutId);

      const initialRevealTimeoutId = window.setTimeout(() => {
        if (revealMessageIntervalId) {
          window.clearInterval(revealMessageIntervalId);
          revealMessageIntervalId = null;
        }
        if (revealStatusElement) {
          revealStatusElement.hidden = true;
          revealStatusElement.classList.remove("is-visible", "is-fading");
        }
        queueRevealForIndex(0, 0);
      }, revealGapMs);
      revealTimeouts.push(initialRevealTimeoutId);
    };

    const revealWhenInViewport = (visibleMatches) => {
      stopRevealCycle();
      if (!visibleMatches || !visibleMatches.length || !matchesElement) return;

      const currentRunId = revealRunId;
      const beginReveal = () => runStaggeredReveal(visibleMatches, currentRunId);

      if (isElementInViewport(matchesElement)) {
        beginReveal();
        return;
      }

      revealObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry && entry.isIntersecting) {
            if (revealObserver) {
              revealObserver.disconnect();
              revealObserver = null;
            }
            beginReveal();
          }
        },
        {
          root: null,
          threshold: 0.2,
        }
      );
      revealObserver.observe(matchesElement);
    };

    const revealImmediately = (visibleMatches) => {
      stopRevealCycle();
      if (!visibleMatches || !visibleMatches.length) return;

      visibleMatches.forEach((match) => {
        match.classList.add(
          "draws--match--revealed",
          "draws--match--reveal-left",
          "draws--match--reveal-vs",
          "draws--match--reveal-right"
        );
      });
    };

    const syncActiveRoundView = () => {
      updateIntro();
      const visibleMatches = getVisibleMatches() || [];

      const announcementTs = activeRoundSlug
        ? roundAnnouncementMap.get(activeRoundSlug)
        : null;
      const nowSeconds = Math.floor(Date.now() / 1000);
      const showCountdown = Boolean(
        announcementTs && announcementTs > nowSeconds
      );
      const skipRevealAnimation = Boolean(
        (activeRoundSlug && roundSkipRevealMap.get(activeRoundSlug)) ||
          (announcementTs &&
            nowSeconds - announcementTs > revealCutoffAfterAnnouncementSeconds)
      );

      if (!countdownElement || !matchesElement) return;

      stopCountdown();
      stopRevealCycle();
      countdownElement.hidden = !showCountdown;
      matchesElement.hidden = showCountdown;

      if (!showCountdown) {
        if (skipRevealAnimation) {
          revealImmediately(visibleMatches);
        } else {
          revealWhenInViewport(visibleMatches);
        }
        return;
      }

      const renderCountdown = () => {
        const countdownStillActive = updateCountdownDisplay(announcementTs);

        if (!countdownStillActive) {
          // When the countdown reaches zero while the page is open,
          // refresh once so the draws are visible without user interaction.
          if (typeof window !== "undefined" && !window.__drawsCountdownReloaded) {
            window.__drawsCountdownReloaded = true;
            window.location.reload();
          }
        }
      };

      renderCountdown();
      countdownIntervalId = window.setInterval(renderCountdown, 1000);
    };

    const setActiveRound = (slug, { updateHash = false } = {}) => {
      const button = findEnabledButtonForSlug(slug);
      if (!button) return false;

      const roundSlug = button.getAttribute("data-round-slug");
      filterButtons.forEach((btn) => btn.classList.remove("active"));
      button.classList.add("active");
      activeRoundSlug = roundSlug;

      if (updateHash) {
        updateHashForRound(roundSlug);
      }

      syncActiveRoundView();
      return true;
    };

    const resolveInitialRoundSlug = () => {
      const hashSlug = getRoundSlugFromHash();
      if (hashSlug && findEnabledButtonForSlug(hashSlug)) {
        return hashSlug;
      }

      if (twigActiveButton && !twigActiveButton.disabled) {
        return twigActiveButton.getAttribute("data-round-slug");
      }

      return firstEnabledButton
        ? firstEnabledButton.getAttribute("data-round-slug")
        : null;
    };

    filterButtons.forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) return;
        const roundSlug = button.getAttribute("data-round-slug");
        setActiveRound(roundSlug, { updateHash: true });
      });
    });

    const initialRoundSlug = resolveInitialRoundSlug();
    if (initialRoundSlug) {
      setActiveRound(initialRoundSlug, { updateHash: false });
    } else {
      syncActiveRoundView();
    }

    window.addEventListener("hashchange", () => {
      const hashSlug = getRoundSlugFromHash();
      if (!hashSlug) return;
      setActiveRound(hashSlug, { updateHash: false });
    });
  });
}
